// src/components/ComprobanteViewerModal.tsx
// Visor de comprobantes (imagen o PDF) en un modal, sin salir de la app.
// Se renderiza vía portal al body para quedar por encima de todo.

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';

interface ComprobanteViewerModalProps {
  url: string | null;
  onClose: () => void;
}

const ComprobanteViewerModal: React.FC<ComprobanteViewerModalProps> = ({ url, onClose }) => {
  const [imgError, setImgError] = useState(false);

  // Resetear el estado de error cada vez que cambia el comprobante
  useEffect(() => { setImgError(false); }, [url]);

  if (!url) return null;

  const isPdf = /\.pdf($|\?)/i.test(url) || /\/raw\//i.test(url);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10010] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">Comprobante de pago</h3>
          <div className="flex items-center gap-1">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Abrir en pestaña nueva"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-2">
          {isPdf ? (
            <iframe src={url} title="Comprobante" className="w-full h-[70vh] border-0 rounded-lg bg-white" />
          ) : imgError ? (
            <div className="text-center py-12 px-6">
              <p className="text-sm text-gray-500 mb-3">No se pudo previsualizar el comprobante.</p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" /> Abrir en una pestaña nueva
              </a>
            </div>
          ) : (
            <img
              src={url}
              alt="Comprobante de pago"
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
              onError={() => setImgError(true)}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ComprobanteViewerModal;
